import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ExerciseDatabase = ({ onDragStart, searchTerm, onSearchChange, selectedCategory, onCategoryChange }) => {
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  const exerciseCategories = [
    { value: 'all', label: 'All Categories' },
    { value: 'upper-body', label: 'Upper Body' },
    { value: 'lower-body', label: 'Lower Body' },
    { value: 'core', label: 'Core & Stability' },
    { value: 'cardio', label: 'Cardiovascular' },
    { value: 'balance', label: 'Balance & Coordination' },
    { value: 'flexibility', label: 'Flexibility & Mobility' }
  ];

  const exerciseDatabase = [
    {
      id: 'ex-001',
      name: 'Seated Leg Extensions',
      category: 'lower-body',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'Leg Extension Machine',
      muscleGroups: ['Quadriceps', 'Knee Stabilizers'],
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      description: 'Seated leg extension exercise targeting quadriceps muscles with controlled movement.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 20 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 25 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 30 }
      }
    },
    {
      id: 'ex-002',
      name: 'Chest Press Machine',
      category: 'upper-body',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'Chest Press Machine',
      muscleGroups: ['Pectorals', 'Anterior Deltoids', 'Triceps'],
      image: 'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&h=300&fit=crop',
      description: 'Machine-based chest press exercise for upper body strength development.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 15 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 20 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 25 }
      }
    },
    {
      id: 'ex-003',
      name: 'Seated Row',
      category: 'upper-body',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'Cable Row Machine',
      muscleGroups: ['Latissimus Dorsi', 'Rhomboids', 'Biceps'],
      image: 'https://images.pexels.com/photos/1552252/pexels-photo-1552252.jpeg?w=400&h=300&fit=crop',
      description: 'Seated rowing exercise for back muscle development and posture improvement.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 18 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 22 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 28 }
      }
    },
    {
      id: 'ex-004',
      name: 'Standing Balance',
      category: 'balance',
      type: 'static',
      difficulty: 'beginner',
      equipment: 'Balance Pad',
      muscleGroups: ['Core', 'Ankle Stabilizers', 'Hip Stabilizers'],
      image: 'https://images.pixabay.com/photo/2017/08/07/14/02/man-2604149_1280.jpg?w=400&h=300&fit=crop',
      description: 'Static balance exercise to improve stability and proprioception.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 15 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 20 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 25 }
      }
    },
    {
      id: 'ex-005',
      name: 'Arm Circles',
      category: 'upper-body',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'None',
      muscleGroups: ['Shoulders', 'Upper Back'],
      image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      description: 'Dynamic arm movement exercise for shoulder mobility and warm-up.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 10 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 15 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 20 }
      }
    },
    {
      id: 'ex-006',
      name: 'Seated Marching',
      category: 'cardio',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'Chair',
      muscleGroups: ['Hip Flexors', 'Core', 'Legs'],
      image: 'https://images.pexels.com/photos/6111597/pexels-photo-6111597.jpeg?w=400&h=300&fit=crop',
      description: 'Seated cardiovascular exercise mimicking marching motion.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 12 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 18 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 24 }
      }
    },
    {
      id: 'ex-007',
      name: 'Wall Push-ups',
      category: 'upper-body',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'Wall',
      muscleGroups: ['Pectorals', 'Triceps', 'Shoulders'],
      image: 'https://images.pixabay.com/photo/2016/11/19/12/43/fitness-1839874_1280.jpg?w=400&h=300&fit=crop',
      description: 'Modified push-up exercise performed against a wall for upper body strength.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 8 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 12 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 16 }
      }
    },
    {
      id: 'ex-008',
      name: 'Ankle Pumps',
      category: 'flexibility',
      type: 'dynamic',
      difficulty: 'beginner',
      equipment: 'None',
      muscleGroups: ['Calves', 'Ankle Stabilizers'],
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      description: 'Ankle mobility exercise to improve circulation and flexibility.',
      phases: {
        'phase-1': { minReps: 2, maxReps: 4, startingIntensity: 5 },
        'phase-2': { minReps: 6, maxReps: 8, startingIntensity: 8 },
        'phase-3': { minReps: 10, maxReps: 12, startingIntensity: 12 }
      }
    }
  ];

  const filteredExercises = useMemo(() => {
    return exerciseDatabase?.filter(exercise => {
      const matchesSearch = exercise?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          exercise?.muscleGroups?.some(muscle => muscle?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
                          exercise?.equipment?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || exercise?.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const handleDragStart = (e, exercise) => {
    e?.dataTransfer?.setData('application/json', JSON.stringify(exercise));
    e.dataTransfer.effectAllowed = 'copy';
    if (onDragStart) {
      onDragStart(exercise);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-success/10 text-success border-success/20';
      case 'intermediate': return 'bg-warning/10 text-warning border-warning/20';
      case 'advanced': return 'bg-error/10 text-error border-error/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getTypeIcon = (type) => {
    return type === 'dynamic' ? 'Activity' : 'Clock';
  };

  return (
    <div className="h-full flex flex-col bg-surface border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Exercise Database</h2>
            <p className="text-sm text-muted-foreground">
              {filteredExercises?.length} exercises available
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              iconName="Grid3X3"
              className="breathing-focus"
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              iconName="List"
              className="breathing-focus"
            >
              List
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="space-y-4">
          <Input
            type="search"
            placeholder="Search exercises, muscles, or equipment..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full"
          />
          
          <Select
            options={exerciseCategories}
            value={selectedCategory}
            onChange={onCategoryChange}
            placeholder="Filter by category"
            className="w-full"
          />
        </div>
      </div>
      {/* Exercise List */}
      <div className="flex-1 overflow-y-auto p-6">
        {filteredExercises?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No exercises found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms or category filter
            </p>
          </div>
        ) : (
          <div className={`
            ${viewMode === 'grid' ?'grid grid-cols-1 xl:grid-cols-2 gap-4' :'space-y-3'
            }
          `}>
            {filteredExercises?.map((exercise) => (
              <div
                key={exercise?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, exercise)}
                className={`
                  group cursor-grab active:cursor-grabbing
                  bg-surface border border-border rounded-lg p-4
                  hover:border-primary/50 hover:shadow-md
                  transition-all duration-200 ease-out
                  breathing-focus
                  ${viewMode === 'grid' ? 'flex flex-col' : 'flex items-center space-x-4'}
                `}
              >
                {/* Exercise Image */}
                <div className={`
                  ${viewMode === 'grid' ? 'w-full h-32 mb-4' : 'w-16 h-16 flex-shrink-0'}
                  bg-muted rounded-lg overflow-hidden
                `}>
                  <Image
                    src={exercise?.image}
                    alt={exercise?.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </div>

                {/* Exercise Info */}
                <div className={`${viewMode === 'grid' ? 'flex-1' : 'flex-1 min-w-0'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {exercise?.name}
                    </h3>
                    <Icon 
                      name={getTypeIcon(exercise?.type)} 
                      size={16} 
                      className="text-muted-foreground flex-shrink-0 ml-2" 
                    />
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {exercise?.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`
                      px-2 py-1 text-xs font-medium rounded-full border
                      ${getDifficultyColor(exercise?.difficulty)}
                    `}>
                      {exercise?.difficulty}
                    </span>
                    
                    <span className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-full">
                      {exercise?.equipment}
                    </span>
                  </div>

                  {/* Muscle Groups */}
                  <div className="flex flex-wrap gap-1">
                    {exercise?.muscleGroups?.slice(0, 2)?.map((muscle, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full"
                      >
                        {muscle}
                      </span>
                    ))}
                    {exercise?.muscleGroups?.length > 2 && (
                      <span className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-full">
                        +{exercise?.muscleGroups?.length - 2} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Drag Indicator */}
                <div className="flex items-center justify-center w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors">
                  <Icon name="GripVertical" size={16} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Drag exercises to add them to your plan</span>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="Activity" size={14} />
              <span>Dynamic</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>Static</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDatabase;